import { AxiosError } from 'axios'
import cn from 'classnames'
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import BaseModal from 'ext-ui/components/BaseModal/BaseModal'
import Button from 'ext-ui/components/Button/Button'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import AlertTriangleIcon from 'ext-ui/svg/icon/AlertTriangleIcon'
import moment from 'moment'
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { useConfirmCardDetails } from 'src/api/mutation/creditcards'
import PhysicalCardVerifyStep from 'src/app/ReviewEnrollCard/PhysicalCardVerifyStep'
import PhysicalCardAcknowledgements from 'src/feature/PhysicalCardAcknowledgements/PhysicalCardAcknowledgements'
import { getLocaleInfo } from 'src/localization/locales'
import { useTranslate } from 'src/localization/Trns'
import { PhysicalCardStatus } from 'src/models/creditCard.models'
import {
  isRecoverableEnrollmentFailure,
  isSettledPhysicalCardStatus,
  isTerminalEnrollmentFailure,
} from 'src/queries/creditCard.derivations'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import {
  stringIsShortDate,
  stringIsShortDateInFuture,
} from 'src/utils/validation/date.validation'
import {
  stringIsEmpty,
  stringIsLuhnValid,
  stringLengthIsBetween,
} from 'src/utils/validation/string.validation'

import style, { visuallyHidden } from './ConfirmCardDetailsModal.style'

const ANALYTICS_EVENT = 'confirm card details'

const formatCardNumber = (val: string): string =>
  val
    .replace(/\D/g, '')
    .slice(0, 16)
    .replace(/(\d{4})(?=\d)/g, '$1 ')

// Hardcoded to a 2-digit/2-digit MM/YY mask rather than derived from
// `getLocaleInfo(locale).shortDateFormat` — unlike the validators below, which are genuinely
// locale-aware. All three supported locales use `MM/YY` today, so this is a deliberate
// simplification, not an oversight; revisit if a locale ever ships a different short-date shape.
const formatExpiry = (val: string): string => {
  const digits = val.replace(/\D/g, '').slice(0, 4)
  return digits.length > 2
    ? `${digits.slice(0, 2)}/${digits.slice(2, 4)}`
    : digits
}

const restrictCvcDigits = (val: string): string =>
  val.replace(/\D/g, '').slice(0, 4)

export type ConfirmCardDetailsModalProps = {
  className?: string
  isOpen: boolean
  onClose: () => void
  creditCardId: string
  /** Names the card being confirmed. Falls back to generic copy when absent. */
  last4?: string
  onVerifyStart?: () => void
  /**
   * Fired once the enroll / 3DS step has settled. Check `physicalCardStatus` before treating
   * this as success: `NEEDS_CVS_VERIFICATION` means the EX-code step is still outstanding and
   * the card is not active (EX-40942).
   */
  onVerifyComplete?: (
    cardId: string,
    physicalCardStatus?: PhysicalCardStatus,
  ) => void
  onVerifyError?: () => void
}

/**
 * Generic "confirm your card details" modal — NOT issuer-specific. All three fields (card number,
 * expiry, CVC) are always collected and always required; `missingCardDetails` decides whether this
 * modal opens at all, not what it renders (EX-40641).
 *
 * Two steps: a `details` step collects card fields, then an `acknowledgements` step (shared
 * `PhysicalCardAcknowledgements`, same copy as the standard `ReviewEnrollCard` connect flow) gates
 * the actual submit — consent is requested separately from data entry, matching how the standard
 * flow separates card review from acknowledgement.
 *
 * Submits to the confirm-card-details endpoint (which also enables the physical feed server-side
 * in the same call), then hands off to the same `PhysicalCardVerifyStep` machinery a fresh
 * enrollment uses so 3DS/CVS verification proceeds identically either way — safe even if it falls
 * through to a fresh `/physical` PUT instead of reusing the in-flight verification, since that PUT
 * is itself idempotent on PAN and `assertNoMissingCardDetails` no longer blocks it once details
 * are confirmed. See EX-40639.
 */
function ConfirmCardDetailsModal(
  props: ConfirmCardDetailsModalProps,
): ReactElement {
  const {
    className = '',
    isOpen,
    onClose,
    creditCardId,
    last4,
    onVerifyStart,
    onVerifyComplete,
    onVerifyError,
  } = props

  const [step, setStep] = useState<'details' | 'acknowledgements'>('details')
  // Field errors stay hidden until the user tries (and fails) to advance past this step, and
  // hide again the moment they edit any field — so errors only ever appear right after a
  // failed Next click, never live while typing (e.g. "12/3" mid-typing an expiry).
  const [showDetailsErrors, setShowDetailsErrors] = useState(false)
  const [cardNumber, setCardNumber] = useState('')
  const [expiry, setExpiry] = useState('')
  const [cvc, setCvc] = useState('')
  const [acknowledgements, setAcknowledgements] = useState({
    cardNetwork: false,
    termsAndConditions: false,
  })
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [verifyingCardId, setVerifyingCardId] = useState('')

  // Which of the three panes is on screen. Verification isn't a `step` value because it's
  // driven by having a card id to verify, but for focus purposes it's a step like any other.
  const currentStep = verifyingCardId ? 'verify' : step
  const stepHeadingRef = useRef<HTMLHeadingElement>(null)
  const previousStepRef = useRef<typeof currentStep | null>(null)

  // Each pane replaces the last inside the same dialog, so the control the user was on
  // unmounts and focus falls to <body> — a keyboard user loses their place and a screen-reader
  // user hears nothing about the new step. Move focus to the incoming heading instead
  // (tabIndex={-1} lets it take focus without becoming a tab stop).
  //
  // Skipped on open: BaseModal owns initial focus, and previousStepRef is cleared in the reset
  // effect above so reopening the modal is never mistaken for a transition.
  useEffect(() => {
    if (!isOpen) return
    if (
      previousStepRef.current !== null &&
      previousStepRef.current !== currentStep
    ) {
      stepHeadingRef.current?.focus()
    }
    previousStepRef.current = currentStep
  }, [isOpen, currentStep])

  const locale = useSelector(selectNullSafeLocaleFromState)
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const { mutateAsync: confirmCardDetails, isPending } = useConfirmCardDetails()

  // useLayoutEffect, not useEffect: this resets PAN/CVC/CVC-adjacent state, and useEffect commits
  // after paint — a reopen would otherwise render one frame with the prior session's card data
  // still on screen before the reset lands. Pure state reset (no network/cache work), so the
  // synchronous-pre-paint tradeoff is exactly what useLayoutEffect is for.
  useLayoutEffect(() => {
    if (!isOpen) return
    analytics.track(ANALYTICS_EVENT, { action: 'viewed' })
    // The modal is kept mounted by its parent (only `isOpen` toggles) — reset on open, not
    // close, so every open starts fresh regardless of what state close left behind (e.g. an
    // in-flight confirm request that resolves after the user has already closed the modal).
    setStep('details')
    // Cleared so the step-focus effect below treats this open as a fresh start rather than a
    // transition from whatever step the previous session ended on.
    previousStepRef.current = null
    setShowDetailsErrors(false)
    setCardNumber('')
    setExpiry('')
    setCvc('')
    setAcknowledgements({ cardNetwork: false, termsAndConditions: false })
    setErrorMessage(undefined)
    setVerifyingCardId('')
  }, [isOpen, analytics])

  const luhnResult = stringIsLuhnValid(cardNumber)
  // stringIsShortDate only compares the digit groups actually present, so a partial "MM" (no
  // year) passes it — moment.js then defaults the missing year to the current one. Require the
  // full MM/YY shape before handing off to the locale-aware validators. Hardcoded to 2/2 digits
  // for the same reason as `formatExpiry` above — all three locales use `MM/YY` today.
  const expiryComplete = expiry === '' || /^\d{2}\/\d{2}$/.test(expiry)
  const expiryResult = expiryComplete
    ? stringIsShortDate(locale)(expiry) ||
      stringIsShortDateInFuture(locale)(expiry)
    : translate('Must be of the form ${dateFormat}', {
        dateFormat: getLocaleInfo(locale).shortDateFormat,
      })
  const cvcResult = stringLengthIsBetween(3, 4)(cvc)

  // One source for both "is this field wrong" and "can we advance" — deriving the second from the
  // first keeps the blank check from living in two places per field.
  const fieldErrors = {
    cardNumber: stringIsEmpty()(cardNumber) ?? luhnResult,
    expiry: stringIsEmpty()(expiry) ?? expiryResult,
    cvc: stringIsEmpty()(cvc) ?? cvcResult,
  }
  const isDetailsInvalid = Object.values(fieldErrors).some(Boolean)

  const isAcknowledgementsInvalid =
    !acknowledgements.cardNetwork || !acknowledgements.termsAndConditions

  const handleClose = (): void => {
    analytics.track(ANALYTICS_EVENT, { action: 'dismissed' })
    onClose()
  }

  const handleNext = (): void => {
    if (isDetailsInvalid) {
      setShowDetailsErrors(true)
      return
    }
    analytics.track(ANALYTICS_EVENT, { action: 'details_confirmed' })
    setStep('acknowledgements')
  }

  const handleBack = (): void => {
    setStep('details')
  }

  // PhysicalCardVerifyStep hands back machine sentinels ('ERROR_FAILED', 'ENROLLMENT_TIMEOUT'),
  // not user-facing copy — map them to the same fallback text the standard connect flow shows,
  // rather than rendering the sentinel verbatim. Translated once per render (not memoized) so the
  // strings below stay primitive values, not fresh function references — that keeps them safe as
  // useCallback deps without defeating the memoization.
  const enrollmentFailedMessage = translate(
    'Card enrollment failed - Try again later',
  )

  // Memoized so PhysicalCardVerifyStep's effects (timeout, Pusher subscription) don't tear down
  // on every parent render — see the identical comment on ReviewEnrollCard's handleCardEnrollError,
  // which this mirrors. An unmemoized callback here would reset the 60s enrollment timeout
  // indefinitely and cause Pusher channel.unbind/bind churn on every render.
  const handleVerifyStepError = useCallback(
    (message: string): void => {
      setVerifyingCardId('')
      setErrorMessage(
        message === 'ERROR_FAILED' || message === 'ENROLLMENT_TIMEOUT'
          ? enrollmentFailedMessage
          : message,
      )
      onVerifyError?.()
    },
    [onVerifyError, enrollmentFailedMessage],
  )

  const handleSubmit = async (): Promise<void> => {
    analytics.track(ANALYTICS_EVENT, { action: 'submitted' })
    setErrorMessage(undefined)
    try {
      const response = await confirmCardDetails({
        creditCardId,
        data: {
          issuerFields: { cardNumber: cardNumber.replace(/\s+/g, '') },
          expires: moment(expiry, getLocaleInfo(locale).shortDateFormat)
            .endOf('month')
            .format('YYYY-MM-DD'),
          cvc,
        },
      })
      analytics.track(ANALYTICS_EVENT, { action: 'succeeded' })

      const settledStatus = response.creditCard?.physicalCard?.status
      if (isSettledPhysicalCardStatus(settledStatus)) {
        onVerifyComplete?.(creditCardId, settledStatus ?? undefined)
        return
      }

      // EX-40942: the integrator rejected this attempt in a way re-entering details cannot fix
      // (issuer declined the verification auth, or the postal code the flow cannot change was
      // rejected). Settled for this attempt: close and let the parent refetch so Account Settings
      // renders the matching blocked-state copy. Handing off would send a second PUT /physical
      // with the same details and land in a generic error / timeout instead.
      if (
        isTerminalEnrollmentFailure(response.creditCard?.physicalCard ?? {})
      ) {
        onVerifyComplete?.(creditCardId, settledStatus ?? undefined)
        return
      }

      // EX-40694: enrollment already failed synchronously (the integrator rejected the details in
      // this same request, landing the card in NOT_APPLICABLE + a recoverable reason). This is
      // terminal for this attempt — surface the failure now. Handing off would send
      // PhysicalCardVerifyStep to re-enroll via PUT /physical with the CVC the integrator just
      // rejected; that second call emits a PHYSICAL_ACCOUNT/CREATED event the step ignores (it
      // terminates only on UPDATED/FAILED), leaving the user on a spinner until the 60s timeout.
      // The retry surfaces stay available underneath, so the user can try again deliberately.
      if (
        isRecoverableEnrollmentFailure(response.creditCard?.physicalCard ?? {})
      ) {
        setErrorMessage(enrollmentFailedMessage)
        onVerifyError?.()
        return
      }

      // Not yet ACTIVE/NEEDS_CVS_VERIFICATION — hand off to PhysicalCardVerifyStep. On mount it
      // reuses the in-flight verification when it finds NEEDS_VERIFICATION + a verificationId;
      // otherwise it falls through to its own PUT /physical, which is safe here since the confirm
      // call above already persisted the details assertNoMissingCardDetails checks for.
      onVerifyStart?.()
      setVerifyingCardId(creditCardId)
    } catch (error: unknown) {
      analytics.track(ANALYTICS_EVENT, { action: 'failed' })
      const unwrappedError = error as AxiosError
      const errorData = unwrappedError.response?.data as { error?: string }
      setErrorMessage(
        errorData?.error ||
          translate('Could not confirm card details - Try again later'),
      )
    }
  }

  // Blocks close for the whole in-flight window, not just once PhysicalCardVerifyStep has
  // mounted: while `isPending` is true (the confirm request itself) `verifyingCardId` is still
  // '', so closing here would let `onVerifyStart?.()` set the parent's `isVerifying` guard after
  // the modal is already closed, with nothing left to ever clear it.
  const isBusy = isPending || !!verifyingCardId

  return (
    <BaseModal
      className={cn('ConfirmCardDetailsModal', className)}
      variant="popup"
      isOpen={isOpen}
      onClose={isBusy ? (): void => undefined : handleClose}
      shouldCloseOnEsc={!isBusy}
      shouldCloseOnOverlayClick={!isBusy}
      showCloseButton={!isBusy}
      closeButtonId="confirm-card-details-close"
      css={style}
      aria-label={translate('Confirm Card Details Modal')}
    >
      {verifyingCardId ? (
        <>
          {/* PhysicalCardVerifyStep is a progress spinner with no heading of its own, and it's
              shared with ReviewEnrollCard — so the focus target for this step lives here
              rather than restructuring that component for one caller. */}
          <h2 css={visuallyHidden} ref={stepHeadingRef} tabIndex={-1}>
            {translate('Verifying your card')}
          </h2>
          <PhysicalCardVerifyStep
            cardId={verifyingCardId}
            eligibilityType="PHYSICAL_ONLY"
            onCardEnrollError={handleVerifyStepError}
            onVerifyStart={onVerifyStart}
            onVerifyError={handleVerifyStepError}
            onVerifyComplete={onVerifyComplete}
          />
        </>
      ) : step === 'details' ? (
        <form
          className="flex-column gap-6"
          onSubmit={(e): void => {
            e.preventDefault()
            handleNext()
          }}
        >
          <h2 className="font-h2" ref={stepHeadingRef} tabIndex={-1}>
            {translate('Confirm Card Details')}
          </h2>
          <p className="font-regular">
            {last4
              ? translate(
                  'To enable physical card transactions, please confirm the details for the card ending in ${last4}. This is a one-time confirmation.',
                  { last4 },
                )
              : translate(
                  'To enable physical card transactions, please confirm your card details below. This is a one-time confirmation.',
                )}
          </p>
          <TextInput
            label={translate('Card Number')}
            value={cardNumber}
            onChange={(e): void => {
              setCardNumber(formatCardNumber(e.target.value))
              setShowDetailsErrors(false)
            }}
            errorText={showDetailsErrors ? fieldErrors.cardNumber : undefined}
            inputMode="numeric"
            requirement="required"
            autoComplete="cc-number"
            testId="confirm-card-details-card-number"
          />
          <div className="fields-row flex-row gap-4">
            <TextInput
              label={translate('Expiration Date')}
              placeholder={getLocaleInfo(locale).shortDateFormat}
              value={expiry}
              onChange={(e): void => {
                setExpiry(formatExpiry(e.target.value))
                setShowDetailsErrors(false)
              }}
              errorText={showDetailsErrors ? fieldErrors.expiry : undefined}
              inputMode="numeric"
              requirement="required"
              autoComplete="cc-exp"
              testId="confirm-card-details-expiry"
            />
            <TextInput
              label={translate('CVC')}
              placeholder="123"
              value={cvc}
              onChange={(e): void => {
                setCvc(restrictCvcDigits(e.target.value))
                setShowDetailsErrors(false)
              }}
              errorText={showDetailsErrors ? fieldErrors.cvc : undefined}
              inputMode="numeric"
              requirement="required"
              autoComplete="cc-csc"
              testId="confirm-card-details-cvc"
            />
          </div>
          <div className="flex-row justify-between align-center">
            <Button variant="no-shell" onClick={handleClose}>
              {translate('Cancel')}
            </Button>
            <Button type="submit" data-testid="confirm-card-details-next">
              {translate('Next')}
            </Button>
          </div>
        </form>
      ) : (
        <form
          className="flex-column gap-6"
          onSubmit={(e): void => {
            e.preventDefault()
            handleSubmit()
          }}
        >
          <h2 className="font-h2" ref={stepHeadingRef} tabIndex={-1}>
            {translate('Acknowledgements:')}
          </h2>
          <PhysicalCardAcknowledgements
            value={acknowledgements}
            onChange={setAcknowledgements}
          />
          {errorMessage && (
            <AlertPanel variant="severe">
              <div className="flex-row justify-start gap-4" role="alert">
                <AlertTriangleIcon />
                <p>{errorMessage}</p>
              </div>
            </AlertPanel>
          )}
          <div className="flex-row justify-between align-center">
            <Button
              variant="no-shell"
              data-testid="confirm-card-details-back"
              disabled={isPending}
              onClick={handleBack}
            >
              {translate('Back')}
            </Button>
            <Button
              type="submit"
              data-testid="confirm-card-details-submit"
              loading={isPending}
              disabled={isPending || isAcknowledgementsInvalid}
            >
              {translate('Confirm')}
            </Button>
          </div>
        </form>
      )}
    </BaseModal>
  )
}

export default ConfirmCardDetailsModal
